import type { Mode, Op } from "../content/modes";
import type { Question, Tier } from "./questions/question";
import { sampleQuestion } from "./questions/sampler";
import { questionType, typesFor } from "./questions/types";
import type { Rng } from "./rng";
import type { Answer } from "./round";

/*
  Placement: a short, untimed check that suggests a starting rank per strand.
  For each strand it asks QUESTIONS_PER_TIER questions at tier 1; with at
  least PASS_PER_TIER right it moves up a tier, otherwise that strand is done.
  It stops after MAX_TIER, so even a strong child has real trials ahead.
*/

export const QUESTIONS_PER_TIER = 4;
export const PASS_PER_TIER = 3;
export const MAX_TIER = 4;

/** Suggested rank by highest tier passed (index 0 = none passed). */
export const RANK_FOR_TIER = [0, 1, 3, 5, 6] as const;

function answersAt(log: readonly Answer[], op: Op, tier: number) {
  return log.filter((a) => {
    const t = questionType(a.typeId);
    return t?.op === op && t.tier === tier;
  });
}

/** Highest tier passed in a strand so far, and whether that strand is finished. */
function progress(log: readonly Answer[], op: Op): { passed: number; done: boolean; tier: number } {
  for (let tier = 1; tier <= MAX_TIER; tier++) {
    const at = answersAt(log, op, tier);
    const wrong = at.filter((a) => !a.correct).length;
    if (wrong > QUESTIONS_PER_TIER - PASS_PER_TIER) return { passed: tier - 1, done: true, tier };
    if (at.length < QUESTIONS_PER_TIER) return { passed: tier - 1, done: false, tier };
  }
  return { passed: MAX_TIER, done: true, tier: MAX_TIER };
}

/** The placement's question source for a round: null once every strand is done. */
export function placementSource(ops: readonly Op[], rng: Rng) {
  return (previous: Question | null, log: readonly Answer[]): Question | null => {
    for (const op of ops) {
      const p = progress(log, op);
      if (p.done) continue;
      return sampleQuestion({
        types: typesFor([op]),
        mix: { [p.tier as Tier]: 100 },
        rng,
        previous,
      });
    }
    return null;
  };
}

/**
 * Suggested ranks from a finished placement. Mix, when more than one strand
 * was checked, gets the lowest of them.
 */
export function placementResult(ops: readonly Op[], log: readonly Answer[]) {
  const out: Partial<Record<Mode, number>> = {};
  for (const op of ops) out[op] = RANK_FOR_TIER[progress(log, op).passed]!;
  if (ops.length > 1) out.mix = Math.min(...ops.map((op) => out[op]!));
  return out;
}
